import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Consistent "navigate back" behaviour for the whole app.
 *
 * Always navigates to the computed parent route rather than stepping through
 * browser history. This is deterministic: detail pages push extra history
 * entries when they update query params (e.g. on tab changes via
 * `useURLTools`), so a plain `navigate(-1)` would only rewind an in-page
 * query-param change and never reach the parent list.
 *
 * The parent route is derived by stripping a trailing `/details/:id` segment,
 * e.g. `/dashboard/meters/details/42` -> `/dashboard/meters`. Pass `fallbackTo`
 * to override this for non-standard pages.
 */
export const useGoBack = (fallbackTo?: string) => {
  const navigate = useNavigate();
  const location = useLocation();

  const computeParentPath = useCallback((): string => {
    if (fallbackTo) return fallbackTo;

    // Strip a trailing `/details/:id` (or any `/details/...`) segment.
    const detailsIndex = location.pathname.indexOf("/details/");
    if (detailsIndex !== -1) {
      return location.pathname.slice(0, detailsIndex);
    }

    // Otherwise drop the last path segment as a best-effort parent.
    const trimmed = location.pathname.replace(/\/+$/, "");
    const lastSlash = trimmed.lastIndexOf("/");
    return lastSlash > 0 ? trimmed.slice(0, lastSlash) : "/dashboard";
  }, [fallbackTo, location.pathname]);

  const parentPath = computeParentPath();

  const goBack = useCallback(() => {
    navigate(parentPath);
  }, [navigate, parentPath]);

  return { goBack, parentPath };
};

export default useGoBack;
